import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { Stack, Shelf, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Paragraph, Regular, Semibold, Title } from "capitalrx-components-2.0/lib/typography";
import { Button, DataRow, Message, Messages, Paged, Pager, Spinner, useSimpleTableDataSource } from "capitalrx-components-2.0/lib/general";

import { financeGetMemberPaymentsList } from "@/api";
import { Finance } from "@/api/interfaces";
import { PROVIDER_MANAGEMENT_LAYOUT } from "@/constants";
import { AuthenticationContext, ThemeSelectorContext } from "@/context";
import { getThemeButton, getThemeURL, hasActiveProviderAccess, hasClientAccess } from "@/utils";

import styles from "./MedicalClaimsList.module.scss";
import { MedicalClaimRow } from "./MedicalClaimRow";

export const outcomeMap: Record<string, string> = {
  "In Progress": "in_progress",
  "Approved": "approved",
  "Partially Approved": "partially_approved",
  "Denied": "denied",
};

type RowData = {
  raw: Finance.MedicalClaimMemberReportList;
  claim: Finance.MedicalClaimMemberSchema;
};

export const MedicalClaimsList: FC = () => {
  const navigate = useNavigate();
  const { isUserLoggedIn, getUser } = useContext(AuthenticationContext);
  const [isAccountUnderReview, setAccountUnderReview] = useState(false);
  const { primary_color } = useContext(ThemeSelectorContext);
  const { isCompressed } = useResponsive(PROVIDER_MANAGEMENT_LAYOUT);

  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedOutcomes, setSelectedOutcomes] = useState<string[]>([]);
  const [results, setResults] = useState<Finance.MedicalClaimMemberReportList[]>([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const RESULTS_PER_PAGE = 10;

  const externalMemberID = searchParams.get("external_member_id");
  const dob = searchParams.get("member_date_of_birth");
  const providerNpi = searchParams.get("provider_npi");
  const tin = searchParams.get("provider_tax_id_number");

  const toggleOutcome = (label: string) => {
    const normalized = outcomeMap[label];
    setSelectedOutcomes((prev) => {
      if (prev.includes(normalized)) return [];
      return [normalized];
    });
  };

  useEffect(() => {
    setPage(0);
  }, [selectedOutcomes, externalMemberID, dob, providerNpi, tin]);

  const getClaim = useCallback(
    (item: Finance.MedicalClaimMemberReportList): Finance.MedicalClaimMemberSchema => {
      return item.member_medical_claim_payment_report;
    },
  []);

  useEffect(() => {
    const fetchClaims = async () => {
      setLoading(true);
      try {
        if (providerNpi && dob && externalMemberID && tin) {
          const payload: Finance.MemberPaymentsListRequestSchema = {
            external_member_id: externalMemberID,
            member_date_of_birth: dob,
            billing_provider_npi: providerNpi,
            billing_provider_tin: tin,
            results_per_page: RESULTS_PER_PAGE,
            page_number: page + 1,
          };

          const response: Finance.MedicalMemberPaymentsListResultSchema =
            await financeGetMemberPaymentsList(payload);

          setResults(response?.results ?? []);
        } else {
          navigate(getThemeURL("MEDICAL_CLAIMS"));
          setSearchParams(searchParams);
        }
      } catch (error) {
        console.error("Failed to fetch claims:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchClaims();
  }, [page, selectedOutcomes, externalMemberID, dob, providerNpi, tin, navigate, searchParams]);

  useEffect(() => {
    const checkUserAccess = async () => {
      if (!isUserLoggedIn()) {
        setAccountUnderReview(false);
        return;
      }

      const user = await getUser();
      const canAccess = hasActiveProviderAccess(user) || hasClientAccess(user);

      setAccountUnderReview(!canAccess);
    };

    checkUserAccess();
  }, [getUser, isUserLoggedIn]);

  const allRows: ReadonlyArray<DataRow<RowData>> = useMemo(
    () =>
      (results ?? []).map((item) => {
        const claim = getClaim(item);
        const id = claim.medical_claim_id;
        return {
          id,
          data: {
            raw: item,
            claim,
          },
        };
      }),
    [results, getClaim],
  );

  const filteredRows: ReadonlyArray<DataRow<RowData>> = useMemo(() => {
    if (selectedOutcomes.length === 0) return allRows;
    return allRows.filter((row) => {
      const status = row.data.claim.claim_status;
      return selectedOutcomes.includes(status);
    });
  }, [allRows, selectedOutcomes]);

  const baseDataSource = useSimpleTableDataSource<RowData>(filteredRows);

  const dataSource: typeof baseDataSource & Paged = {
    ...baseDataSource,
    page,
    pageSize: RESULTS_PER_PAGE,
    maxRows: filteredRows.length,
  };

  const buildSearchParams = () => {
    const params = new URLSearchParams();
    if (externalMemberID) params.append("external_member_id", externalMemberID);
    if (dob) params.append("member_date_of_birth", dob);
    if (providerNpi) params.append("provider_npi", providerNpi);
    if (tin) params.append("provider_tax_id_number", tin);
    return params.toString();
  };

  return (
    <Stack spacing="large" style={{ paddingBottom: "8px" }}>
      <Stack spacing="small">
        <Shelf style={{ padding: 0 }}>
          <Button
            style={{ padding: 0 }}
            icon="arrow-left"
            appearance={getThemeButton("tertiary", primary_color)}
            onClick={() =>
              navigate({
                pathname: getThemeURL("MEDICAL_CLAIMS"),
                search: buildSearchParams(),
              })
            }
          >
            <Body style={{ color: primary_color, textDecoration: "underline" }}>Search</Body>
          </Button>
          <Shelf.Spacer />
        </Shelf>
        <Title>Claim Results</Title>
        <Shelf>
          <Shelf spacing="mini">
            <Semibold>Member ID:</Semibold>
            <Body>{externalMemberID}</Body>
          </Shelf>
          <Shelf spacing="mini">
            <Semibold>Date of Birth:</Semibold>
            <Body>{dob}</Body>
          </Shelf>
          <Shelf.Spacer />
        </Shelf>
      </Stack>

      {!isUserLoggedIn() && (
        <Message
          style={{ height: isCompressed ? "86px" : "38px", maxHeight: "fit-content" }}
          appearance={Messages.Warning}
          visible
        >
          {isCompressed ? (
            <Stack spacing="small" alignment="left">
              <Regular>
                To view additional payment details, EOPs, and 835 files, log in or create an account
              </Regular>
              <Button
                onClick={() => navigate(getThemeURL("ACCOUNT"))}
                appearance={getThemeButton("tertiary", primary_color)}
              >
                Create an Account
              </Button>
            </Stack>
          ) : (
            <Shelf spacing="small">
              <Regular>
                To view additional payment details, EOPs, and 835 files, log in or create an account
              </Regular>
              <Shelf.Spacer />
              <Button
                onClick={() => navigate(getThemeURL("ACCOUNT"))}
                appearance={getThemeButton("tertiary", primary_color)}
              >
                Create an Account
              </Button>
            </Shelf>
          )}
        </Message>
      )}

      {isAccountUnderReview && (<Message
          style={{ height: isCompressed ? "86px" : "38px", maxHeight: "fit-content" }}
          appearance={Messages.Warning}
          visible
        >
        <Regular>
            Your account is still under review. Once approved, you'll be able to view a list of all cases for this member.
        </Regular>
      </Message>)}
      <Stack spacing="mini">
        <Paragraph className={styles.filterText}>filter by claim status</Paragraph>
        <Stack.Spacer />
        <Shelf spacing="small">
          {Object.keys(outcomeMap).map((label) => {
            const normalized = outcomeMap[label];
            const active = selectedOutcomes.includes(normalized);
            return (
              <button
                key={label}
                onClick={() => toggleOutcome(label)}
                className={active ? styles.StatusFilterActive : styles.StatusFilter}
              >
                {label}
              </button>
            );
          })}
          <Shelf.Spacer />
        </Shelf>
      </Stack>

      {loading && (
        <Stack alignment="center">
          <Spinner size="large" />
        </Stack>
      )}

      {!loading && filteredRows && (
        <Stack>
          {filteredRows.length === 0 && <Body>No Results Found</Body>}

          {filteredRows.length > 0 && (
            <Stack spacing="small">
              {!isCompressed && (
                <Shelf spacing="medium-large" className={styles.ClaimRow}>
                  <Stack alignment="left" spacing="mini" style={{ width: "122px" }}>
                    <Shelf spacing="mini">
                      <Regular className={styles.filterText}>service start</Regular>
                    </Shelf>
                  </Stack>
                  <Shelf.Spacer />
                  <Stack alignment="left" spacing="mini" style={{ width: "138px" }}>
                    <Regular className={styles.filterText}>submission date</Regular>
                  </Stack>
                  <Shelf.Spacer />
                  <Stack alignment="left" spacing="mini" style={{ width: "241px" }}>
                    <Regular className={styles.filterText}>claim status</Regular>
                  </Stack>
                  <Shelf.Spacer />
                  <Stack alignment="right" spacing="mini" style={{ width: "76px" }}>
                    <Regular className={styles.filterText}>amount paid</Regular>
                  </Stack>
                  <Shelf.Spacer />
                  <Stack alignment="left" spacing="mini" style={{ width: "93px" }}>
                    <Shelf spacing="mini">
                      <Regular className={styles.filterText}>payment issued</Regular>
                    </Shelf>
                  </Stack>
                  <Shelf.Spacer />
                  <Stack alignment="center" spacing="mini" style={{ width: "94px" }}>
                    <Regular className={styles.filterText}>payment status</Regular>
                  </Stack>
                </Shelf>
              )}

              {filteredRows.map((row) => (
                <MedicalClaimRow
                  key={row.id}
                  start_date={row.data.claim.start_date}
                  submission_date={row.data.claim.submission_date}
                  claim_status={row.data.claim.claim_status}
                  payment_amount={row.data.claim.payment_amount}
                  issue_date={row.data.claim.issue_date}
                  payment_is_void={row.data.claim.payment_is_void}
                  payment_is_cleared={row.data.claim.payment_is_cleared}
                />
              ))}

              <Pager dataSource={dataSource} />
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
};
